import {useState} from 'react'
import {Checkbox, Icon, Button} from '@/components/atoms'

/**
 * A reusable list component with selectable items, search functionality, and customizable display options.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.title='List'] - The title displayed at the top of the list
 * @param {Array<Object>} [props.items=[]] - Array of items to display in the list
 * @param {number|string} props.items[].id - Unique identifier for each item
 * @param {string} props.items[].label - Main text label for the item
 * @param {string} [props.items[].subLabel] - Optional secondary label (displayed in blue)
 * @param {boolean} [props.items[].checked=false] - Whether the item's checkbox is checked
 * @param {boolean} [props.items[].selected=false] - Whether the item row is selected (highlighted)
 * @param {Function} [props.onItemClick] - Callback when an item row is clicked (only when selectable=true)
 * @param {Function} [props.onCheckboxChange] - Callback when a checkbox state changes
 * @param {string} [props.searchPlaceholder='Search...'] - Placeholder text for the search input
 * @param {boolean} [props.showSearch=true] - Whether to display the search bar
 * @param {string} [props.className=''] - Additional CSS classes to apply to the container
 * @param {boolean} [props.separateItems=false] - Whether to show separators between all items
 * @param {boolean} [props.selectable=true] - Whether items can be clicked for selection (shows chevron icon)
 * @param {boolean} [props.showSelectedCount=false] - Whether to display count of selected items
 * @param {boolean} [props.showPagination=false] - Whether to show pagination controls
 * @param {boolean} [props.selectedCount=0] - Describes number of selected items
 * @param {number} [props.currentPage=0] - Current page number (0-indexed)
 * @param {number} [props.totalPages=0] - Total number of pages
 * @param {Function} [props.onPageChange] - Callback when page changes
 * @param {number} [props.pageSize=10] - Number of items per page
 * @param {number} [props.totalItems=0] - Total number of items across all pages
 * @param {boolean} [props.useExternalSearch=false] - Whether to use external search (disables built-in filtering)
 * @param {Function} [props.onSearchChange] - Callback when search input changes (for external search)
 * @param {boolean} [props.isLoading=false] - Whether the list is in a loading state
 *
 * @example
 * // Basic usage with selectable items
 * <SelectableList
 *   title="Folder"
 *   items={folderItems}
 *   onItemClick={handleItemClick}
 *   onCheckboxChange={handleCheckboxChange}
 * />
 *
 * @example
 * // Checkbox-only list with separators and item count
 * <SelectableList
 *   title="Segments"
 *   items={segmentItems}
 *   selectable={false}
 *   separateItems={true}
 *   showSelectedCount={true}
 *   onCheckboxChange={handleCheckboxChange}
 * />
 *
 * @example
 * // List with pagination
 * <SelectableList
 *   title="Segments"
 *   items={segmentItems}
 *   showPagination={true}
 *   currentPage={0}
 *   totalPages={5}
 *   onPageChange={handlePageChange}
 *   pageSize={10}
 *   totalItems={50}
 * />
 */
const SelectableList = ({
  title = 'List',
  items = [],
  onItemClick = () => {},
  onCheckboxChange = () => {},
  searchPlaceholder = 'Search...',
  showSearch = true,
  className = '',
  separateItems = false, // Boolean to add separator after each item
  selectable = true, // Boolean to determine if items are clickable
  showSelectedCount = false, // Boolean to show selected items count
  showPagination = false, // Boolean to show pagination controls
  selectedCount = 0,
  currentPage = 0,
  totalPages = 0,
  onPageChange = () => {},
  pageSize = 10,
  totalItems = 0,
  useExternalSearch = false, // Whether to use external search (API-based)
  onSearchChange = () => {}, // Callback for external search
  isLoading = false, // Loading state
}) => {
  const [searchTerm, setSearchTerm] = useState('')

  // Filter items based on search term (only if not using external search)
  const filteredItems = useExternalSearch
    ? items // When using external search, items are already filtered by the API
    : items?.filter(item => item?.label?.toLowerCase().includes(searchTerm?.toLowerCase()))

  // Calculate selected items count

  // Calculate pagination info
  const startItem = currentPage * pageSize + 1
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems)

  return (
    <div className={`flex flex-col p-4 text-white ${className}`}>
      {/* Header */}
      {title && <h2 className="mb-4 text-[28px] font-bold">{title}</h2>}

      {/* Selected Count */}
      {showSelectedCount && (
        <div className="mb-3 border-b-2 border-gray-700 pb-3 text-sm text-gray-300">
          {selectedCount} item{selectedCount !== 1 ? '(s)' : ''} selected
        </div>
      )}

      {/* Search Bar */}
      {showSearch && (
        <div className="relative mb-4">
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e?.target?.value)
              if (useExternalSearch) {
                // For external search, call the callback
                onSearchChange(e?.target?.value)
              }
            }}
            className="h-8 w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
          />
          <Icon
            icon="search"
            className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-50"
            size="medium"
          />
        </div>
      )}

      {/* List Items */}
      <div className="flex-1 space-y-1 ">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-gray-400">Loading...</span>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-gray-400">
              {searchTerm ? 'No items found matching your search' : 'No items available'}
            </span>
          </div>
        ) : (
          filteredItems.map((item, index) => (
            <div key={item.id || index}>
              <div
                className={`
                flex cursor-pointer items-center justify-between rounded p-2 transition-colors
                ${selectable && item?.selected ? 'bg-gray-800' : ''}
                ${!item?.selected ? 'hover:bg-gray-800' : ''}
              `}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (selectable) {
                    onItemClick(item)
                  } else {
                    // When not selectable, clicking anywhere toggles the checkbox
                    onCheckboxChange(item?.id || index, !item?.checked)
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    if (selectable) {
                      onItemClick(item)
                    } else {
                      onCheckboxChange(item?.id || index, !item?.checked)
                    }
                  }
                }}
              >
                <div className="flex flex-1 items-center space-x-3">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={e => {
                      e.stopPropagation()
                      // Always handle checkbox click separately to allow direct checkbox interaction
                      onCheckboxChange(item?.id || index, !item?.checked)
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        onCheckboxChange(item?.id || index, !item?.checked)
                      }
                    }}
                  >
                    <Checkbox
                      value={item?.checked || false}
                      updateValue={checked => onCheckboxChange(item?.id || index, checked)}
                      label=""
                      isDark={true}
                    />
                  </div>
                  <div className="flex flex-1 justify-between pr-2">
                    <span className="text-sm font-bold">{item.label}</span>
                    {item.subLabel && (
                      <span className="ml-2 mt-[2px] text-xs font-bold text-[#66A5E7]">{item.subLabel}</span>
                    )}
                  </div>
                </div>
                {selectable && <Icon icon="chevron_right" className="text-[#66A5E7]" size="large" />}
              </div>

              {/* Separator - show after each item except the last one */}
              {separateItems && index < filteredItems?.length - 1 && <div className="my-2 border-b border-gray-700" />}
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {showPagination && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center pt-4">
          <div className="inline-flex items-center rounded border border-gray-700 bg-gray-800">
            {/* Previous Button */}
            <button
              onClick={() => {
                if (currentPage > 0) {
                  onPageChange(currentPage - 1)
                }
              }}
              disabled={currentPage === 0}
              className={`
                flex items-center justify-center rounded-l-lg border-r border-gray-700 transition-colors
                ${
                  currentPage === 0
                    ? 'cursor-not-allowed text-gray-600'
                    : 'cursor-pointer text-[#66A5E7] hover:bg-gray-700'
                }
              `}
              style={{minWidth: 32, minHeight: 32, maxWidth: 32, maxHeight: 32}}
            >
              <Icon icon="chevron_left" size="medium" />
            </button>

            {/* Page Numbers */}
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              // Show first few pages, current page area, and last page
              let pageNum
              if (totalPages <= 5) {
                pageNum = index
              } else if (currentPage < 3) {
                pageNum = index
              } else if (currentPage >= totalPages - 3) {
                pageNum = totalPages - 5 + index
              } else {
                pageNum = currentPage - 2 + index
              }

              if (pageNum < 0 || pageNum >= totalPages) return null

              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`
                    flex items-center justify-center border-r border-gray-700 text-sm font-medium transition-colors
                    ${
                      pageNum === currentPage
                        ? 'bg-[#0069D7] text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }
                  `}
                  style={{minWidth: 32, minHeight: 32, maxWidth: 32, maxHeight: 32}}
                >
                  {pageNum + 1}
                </button>
              )
            })}

            {/* Next Button */}
            <button
              onClick={() => {
                if (currentPage < totalPages - 1) {
                  onPageChange(currentPage + 1)
                }
              }}
              disabled={currentPage === totalPages - 1}
              className={`
                flex items-center justify-center rounded-r-lg transition-colors
                ${
                  currentPage === totalPages - 1
                    ? 'cursor-not-allowed text-gray-600'
                    : 'cursor-pointer text-[#66A5E7] hover:bg-gray-700'
                }
              `}
              style={{minWidth: 32, minHeight: 32, maxWidth: 32, maxHeight: 32}}
            >
              <Icon icon="chevron_right" size="medium" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SelectableList
